import { useEffect, useState } from 'react'

import jwt_decode from "jwt-decode";
import CryptoJS from 'crypto-js';

const oauthClientId = window.auth_client_id
const redirectUrl = `${window.location.origin}`
const fixturesUser = window.fixtures_user

const tokenUrl = `${window.auth_url}/token`
const logoutUrl = `${window.auth_url}/logout?redirect_uri=${redirectUrl}`

const REFRESH_TOKEN_LOCAL_STORAGE = 'am:auth:refresh_token'
const ACCESS_TOKEN_LOCAL_STORAGE = 'am:auth:access_token'
const IS_EMBED_TOKEN = 'am:auth:is_embed'

const enableLocalStorage = true

/**
 * Use package.json version to force user to logout when a new mayor version is released
 * Update the package.json version on line 3 to increase by 1
 */
const checkAppVersion = () => {
    const currentVersion = (process.env.REACT_APP_BUILD_VERSION).toString()
    const storedVersion = localStorage.getItem("appVersion")
    
    /**
     * if version is not in local storage: save it 
     */
    if(storedVersion === undefined || storedVersion === null) {
        localStorage.removeItem("appVersion")
        localStorage.setItem("appVersion", currentVersion)
    }
    /**
     * if version is in storage: check against current
     * if versions are different: log user out
     */
    else {
        if (currentVersion !== storedVersion) {
            localStorage.removeItem("appVersion")
            localStorage.setItem("appVersion", currentVersion)
            oauthLogout()
        } 
    } 
}
checkAppVersion();



export function oauthLogout() {
    // Capture the currently viewed company
    const lastActiveCompany = localStorage.getItem("am:audience:active:company");
    // Clear everything
    localStorage.clear();
    sessionStorage.clear();
    // Reset the active company for when the user logs in again.
    if (lastActiveCompany && Object.keys(lastActiveCompany).length) {
        localStorage.setItem("am:audience:active:company", lastActiveCompany);
    }
    window.location = logoutUrl;
}

export function clearLocalAccessTokens() {
    if (enableLocalStorage) {
        localStorage.removeItem(ACCESS_TOKEN_LOCAL_STORAGE)
        localStorage.removeItem(REFRESH_TOKEN_LOCAL_STORAGE)
        localStorage.removeItem(IS_EMBED_TOKEN)
    }
}

export function getLocalAccessToken() {
    if (enableLocalStorage) {
        return localStorage.getItem(ACCESS_TOKEN_LOCAL_STORAGE)
    }
    return null
}

export function getLocalRefreshToken() {
    if (enableLocalStorage) {
        return localStorage.getItem(REFRESH_TOKEN_LOCAL_STORAGE)
    }
    return null
}

export function getLocalIsEmbedToken() {
    if (enableLocalStorage) {
        return localStorage.getItem(IS_EMBED_TOKEN)
    }
    return null
}

export function setLocalIsEmbedToken(value) {
    if (enableLocalStorage) {
        localStorage.setItem(IS_EMBED_TOKEN, value)
    }
}

export function setLocalAccessToken(token) {
    if (enableLocalStorage) {
        localStorage.setItem(ACCESS_TOKEN_LOCAL_STORAGE, token)
    }
}

export function setLocalRefreshToken(token) {
    if (enableLocalStorage) {
        localStorage.setItem(REFRESH_TOKEN_LOCAL_STORAGE, token)
    }
}

export function saveOriginalRequestUrl() {
    localStorage.setItem("target_url",
        window.location.href.replace(window.location.origin, ''))
}

export function getOriginalRequestUrl() {
    let target = localStorage.getItem("target_url")
    localStorage.removeItem("target_url")

    let params = (new URL(window.location.href)).searchParams;
    if (!target && !!params.get('code')) {
        target = '/'
    }
    return target
}

export default function useAuthTokens(){
    const [ loaded, setLoaded ] = useState(false)
    const [ refreshToken, setRefreshToken ] = useState(null)
    const [ accessToken, setAccessToken ] = useState(null)
    const [requestRefresh, setRequestRefresh] = useState(false)
    const [isEmbedToken, setIsEmbedToken] = useState(false)

    // eslint-disable-next-line
    const [embedCode, setEmbedCode] = useState("");

    const tokens = {
        loaded,
        accessToken,
    }

    useEffect( ()=> {
        if ((window.location.pathname === '/use-fixtures') || localStorage.getItem('useFixtures')) {
            localStorage.setItem('useFixtures', true)
            sessionStorage.setItem('fixture-user', JSON.stringify({
                username: fixturesUser,
                name: 'Fixture User',
            }))
            setLoaded(true)
        }
        else {
            loadTokens()
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect( ()=> {
        if (!!accessToken && !!refreshToken) {
            setLoaded(true)
        }
    }, [accessToken, refreshToken])

    useEffect( ()=> {
        if (requestRefresh && refreshToken) {
            // The current check for embed token vs azure token
            if (isEmbedToken) {
                getNewEmbedToken();
            } else {
                refreshTokens();
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestRefresh, refreshToken])

    function requestNewCode() {
        clearLocalAccessTokens()
        saveOriginalRequestUrl()

        const codeVerifier = (crypto.randomUUID() + crypto.randomUUID()).replaceAll('-','');
        localStorage.setItem('codeVerifier', codeVerifier);

        let newUrl = `${window.auth_url}/authorize`
            newUrl += '?' + encodeNameValue('response_type', 'code')
            newUrl += '&' + encodeNameValue('client_id', oauthClientId)
            newUrl += '&' + encodeNameValue('redirect_uri', redirectUrl)
            newUrl += '&' + encodeNameValue('nonce', 'defaultNonce')
            newUrl += '&' + encodeNameValue('scope', `openid offline_access ${oauthClientId}`)
            newUrl += '&' + encodeNameValue('prompt', 'login')
            newUrl += '&' + encodeNameValue('code_challenge_method', 'S256')
            newUrl += '&' + encodeNameValue('code_challenge',
                CryptoJS.SHA256(codeVerifier).toString(CryptoJS.enc.Base64)
                    .replace(/\+/g, '-')
                    .replace(/\//g, '_')
                    .replace(/=/g, ''))
    
            window.location.href = newUrl;
    }
    
    function getNewEmbedToken() {
        setRequestRefresh(false);
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ refresh_token: refreshToken })
        };
        const url = `${window.api_url}/external_embed/refresh_external_token`;
        fetch(url, requestOptions)
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                    setAccessToken(data.payload.accessToken);
                    setRefreshToken(data.payload.refreshToken);
                    setLocalAccessToken(data.payload.accessToken);
                    setLocalRefreshToken(data.payload.refreshToken);
                    scheduleRefresh(data.payload.accessToken);
                }
            })
            .catch(error => { 
                console.log("Session Expired. Removing Expired Token")
                clearLocalAccessTokens();
                window.isTokenValid = true;
                // custom event signaling that "window.isTokenValid" has changed
                // window.dispatchEvent(new CustomEvent('isTokenValid', { detail: true }));
            });
    }

    function authCodePresent() {
        let params = (new URL(window.location.href)).searchParams;
        const azureCode = params.get('code');
        const embedCode = params.get('et');
        setEmbedCode(embedCode);
        return embedCode || azureCode;
    }

    function encodeNameValue(key, value) {
        let encodedKey = encodeURIComponent(key);
        let encodedValue = encodeURIComponent(value);
        return encodedKey + "=" + encodedValue
    }

    function refreshTokens() {
        setRequestRefresh(false)
        const refresh_token = refreshToken
        if (typeof(refresh_token) !== "undefined" &&
                refresh_token !== "undefined" && refresh_token !== null) {

            let formBody = encodeNameValue('grant_type', 'refresh_token')
            formBody += '&' + encodeNameValue('client_id', 'am_app_ui')
            formBody += '&' + encodeNameValue('refresh_token', refresh_token)
            formBody += '&' + encodeNameValue('redirect_uri', redirectUrl)

            fetch(tokenUrl,  {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: formBody
            })
            .then(response => {
                if (!response.ok) throw new Error('Bad Token');
                return response.json()
            })
            .then(data => {
                // handle the response
                if (data.access_token) {
                    setAccessToken(data.access_token)
                    setRefreshToken(data.refresh_token)
                    setLocalAccessToken(data.access_token)
                    setLocalRefreshToken(data.refresh_token)
                    scheduleRefresh(data.access_token)
                }
            }).catch(function() {
                requestNewCode();
            });
        }
        else {
            console.log("Session Expired. Removing Expired Token")
            clearLocalAccessTokens();
            window.isTokenValid = true;
            // custom event signaling that "window.isTokenValid" has changed
            window.dispatchEvent(new CustomEvent('isTokenValid', { detail: true }));
        }
    }

    function completeLogin() {
        let params = (new URL(window.location.href)).searchParams;
        let code = params.get('code');
        let embedAccessToken = params.get('et');
        let embedRefreshToken = params.get('rt');

        const codeVerifier = localStorage.getItem('codeVerifier');

        if (embedAccessToken) {
            setAccessToken(embedAccessToken)
            setRefreshToken(embedRefreshToken)
            setIsEmbedToken(true)

            setLocalAccessToken(embedAccessToken)
            setLocalRefreshToken(embedRefreshToken)
            setLocalIsEmbedToken(true);

            scheduleRefresh(embedAccessToken)
        } else {
            let formBody = encodeNameValue('grant_type', 'authorization_code')
            formBody += '&' + encodeNameValue('client_id', oauthClientId)
            formBody += '&' + encodeNameValue('code', code)
            formBody += '&' + encodeNameValue('scope', `openid offline_access ${oauthClientId}`)
            formBody += '&' + encodeNameValue('code_verifier', codeVerifier)
            formBody += '&' + encodeNameValue('redirect_uri', redirectUrl)
    
            fetch(tokenUrl,  {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: formBody
            })
            .then(response => {
                if (!response.ok) {
                    throw Error(response.statusText);
                }
                return response.json()
            })
            .then(data => {
                // handle the response
                if (data.access_token) {
                    setAccessToken(data.access_token)
                    setRefreshToken(data.refresh_token)
    
                    setLocalAccessToken(data.access_token)
                    setLocalRefreshToken(data.refresh_token)
    
                    scheduleRefresh(data.access_token)
                }
            }).catch(function() {
                requestNewCode();
            });
        }

    }

    function scheduleRefresh(token) {
        let timeToRefresh = -1
        try {
            const decodedToken = jwt_decode(token)
            timeToRefresh = (decodedToken.exp * 1000) - (Date.now()) - window.token_pre_expire;
        } catch (error) {
            // Ignore for now
        }
        const min = Math.floor(timeToRefresh / 60000);
        const sec = ((timeToRefresh % 60000) / 1000).toFixed(0);
        const time = min + ":" + (sec < 10 ? '0' : '') + sec;
        console.log(`minutes to refresh: ${time}`)
        console.log(`AudienceMaker UI Version: ${process.env.REACT_APP_BUILD_VERSION}`)
        setTimeout(() => setRequestRefresh(true), timeToRefresh)
    }
    // Runs from useEffect on first load
    function loadTokens() {
        // check for embed tokens
        let params = (new URL(window.location.href)).searchParams;
        let embedAccessToken = params.get('et');
        let embedRefreshToken = params.get('rt');
        const isAnEmbedToken = (getLocalIsEmbedToken() || (embedAccessToken && embedRefreshToken)) ? true : false;
        setIsEmbedToken(isAnEmbedToken);

        // set tokens based by either embed or 
        let localAccessToken, localRefreshToken;
        if (!embedAccessToken && !embedRefreshToken) {
            // Pull from localStorage
            localRefreshToken = getLocalRefreshToken()
            localAccessToken = getLocalAccessToken()
        } else {
            // Use supplied query values for embed
            localRefreshToken = embedRefreshToken;
            localAccessToken = embedAccessToken;
        }

        // Check if Refresh token exists
        let validRefreshToken = false
        if (typeof(localRefreshToken) !== "undefined" && localRefreshToken !== "undefined" && localRefreshToken !== null) {
            validRefreshToken = true
        }

        if (validRefreshToken) {
            //Make sure the access token hasn't expired
            const decodedToken = jwt_decode(localAccessToken);
            validRefreshToken = (decodedToken.exp * 1000) - (Date.now()) > 0;
        }

        if (validRefreshToken) {
            setAccessToken(localAccessToken)
            setLocalAccessToken(localAccessToken)
            setRefreshToken(localRefreshToken)

            scheduleRefresh(localAccessToken)
        }
        else {
            console.log("Getting Token")
            if (authCodePresent()) {
                completeLogin();
            }
            else {
                console.log("Refreshing Token")
                // this check is for embed tokens vs. azure token
                if (isAnEmbedToken) {
                    getNewEmbedToken();
                } else {
                    requestNewCode();
                }
            }
        }
    }
    return tokens;
}
